import React, { useState, useEffect } from 'react';
import { CONFIG } from '../constants/config';
import '../styles/AdvancedSettings.css';

interface AdvancedSettingsProps {
  delayMs: number;
  onDelayChange: (delay: number) => void;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({ delayMs, onDelayChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localDelay, setLocalDelay] = useState(delayMs);
  const [password, setPassword] = useState('');
  const PASSWORD = 'thisisunsafe';
  const isUnsafeDelayEnabled = password === PASSWORD;

  const handleDelayChange = (value: number) => {
    // Prevent setting 0ms or 500ms if password is not correct
    if ((value === 0 || value === 500) && !isUnsafeDelayEnabled) {
      return;
    }
    const clampedValue = Math.max(CONFIG.MIN_DELAY_MS, Math.min(CONFIG.MAX_DELAY_MS, value));
    setLocalDelay(clampedValue);
    onDelayChange(clampedValue);
  };

  const resetToDefault = () => {
    handleDelayChange(CONFIG.DEFAULT_DELAY_MS);
  };

  // Reset to default if password is cleared and current delay is unsafe
  useEffect(() => { 
    if (!isUnsafeDelayEnabled && (localDelay === 0 || localDelay === 500)) {
      const clampedValue = CONFIG.DEFAULT_DELAY_MS;
      setLocalDelay(clampedValue);
      onDelayChange(clampedValue);
    }
  }, [isUnsafeDelayEnabled, localDelay, onDelayChange]);

  const getDelayBadge = (delay: number) => {
    if (delay === 0) {
      return {
        className: 'info-badge danger-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        ),
        text: <span><strong>Καθυστέρηση:</strong> 0ms - <strong>Υψηλός Κίνδυνος</strong></span>
      };
    }
    if (delay === 500) {
      return {
        className: 'info-badge warning-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        ),
        text: <span><strong>Καθυστέρηση:</strong> 500ms - <strong>Μέτριος Κίνδυνος</strong></span>
      };
    }
    if (delay === 1000) {
      return {
        className: 'info-badge yellow-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        ),
        text: <span><strong>Καθυστέρηση:</strong> 1s</span>
      };
    }
    if (delay === 3000) {
      return {
        className: 'info-badge safe-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ),
        text: <span><strong>Καθυστέρηση:</strong> 3s - <strong>Προεπιλεγμένη & Ασφαλής</strong></span>
      };
    }
    if (delay === 5000) {
      return {
        className: 'info-badge safest-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ),
        text: <span><strong>Καθυστέρηση:</strong> 5s - <strong>Ασφαλέστερη</strong></span>
      };
    }
    return {
      className: 'info-badge',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      ),
      text: <span><strong>Καθυστέρηση:</strong> {delay}ms</span>
    };
  };

  const getDelayInfoBadge = (delay: number) => {
    if (delay === 0) {
      return {
        className: 'info-badge danger-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        ),
        text: <span><strong>Υψηλός Κίνδυνος:</strong> Δεν θα υπάρχει καθυστέρηση - πολύ υψηλός κίνδυνος rate limiting. Προτείνεται τουλάχιστον 1s καθυστέρηση.</span>
      };
    }
    if (delay === 500) {
      return {
        className: 'info-badge warning-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        ),
        text: <span><strong>Μέτριος Κίνδυνος:</strong> Γρήγορη επεξεργασία - μέτριος κίνδυνος rate limiting. Προτείνεται τουλάχιστον 1s για ασφάλεια.</span>
      };
    }
    if (delay === 1000) {
      return {
        className: 'info-badge yellow-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        ),
        text: <span><strong>1 δευτερόλεπτο:</strong> Ιδανική καθυστέρηση για σταθερή επεξεργασία. Χαμηλό ρίσκο rate limiting.</span>
      };
    }
    if (delay === 3000) {
      return {
        className: 'info-badge safe-badge',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ),
        text: <span><strong>Προεπιλεγμένη & Ασφαλής:</strong> Ασφαλής καθυστέρηση - πολύ χαμηλό ρίσκο rate limiting. Καλή επιλογή για μεγάλα αρχεία. Αυτή είναι η προεπιλεγμένη τιμή.</span>
      };
    }
    return {
      className: 'info-badge safest-badge',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ),
      text: <span><strong>Ασφαλέστερη:</strong> Ασφαλέστερη καθυστέρηση - καμία πιθανότητα rate limiting. Προτεινόμενη για πολύ μεγάλα αρχεία.</span>
    };
  };

  return (
    <div className={`advanced-settings ${isExpanded ? 'expanded' : ''}`}>
      <button 
        className="advanced-settings-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        <div className="toggle-content">
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            className={isExpanded ? 'rotated' : ''}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
          <span>Προχωρημένες Ρυθμίσεις</span>
        </div>
      </button>

      {!isExpanded && (
        <div className="collapsed-info">
          <div className="collapsed-description">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>Χρονική καθυστέρηση μεταξύ των API κλήσεων για αποφυγή rate limiting</span>
          </div>
          {(() => {
            const badge = getDelayBadge(delayMs);
            return (
              <div className={badge.className}>
                {badge.icon}
                {badge.text}
            </div>
            );
          })()}
        </div>
      )}

      {isExpanded && (
        <div className="advanced-settings-content">
          <div className="settings-section">
            <div className="setting-header">
              <div className="setting-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="setting-info">
                <h3>Χρονική Καθυστέρηση μεταξύ Κλήσεων</h3>
                <p className="setting-description">
                  Ρυθμίστε την καθυστέρηση μεταξύ των API κλήσεων για να αποφύγετε rate limiting.
                  Προεπιλεγμένη τιμή: {CONFIG.DEFAULT_DELAY_MS}ms. Χρησιμοποιήστε τις γρήγορες επιλογές για διαφορετικές τιμές.
                </p>
              </div>
            </div>

            <div className="delay-control">
              <div className="delay-input-wrapper">
                <label htmlFor="delay-input">Καθυστέρηση (ms):</label>
                <div className="input-with-buttons">
                  <input
                    id="delay-input"
                    type="number"
                    min={isUnsafeDelayEnabled ? CONFIG.MIN_DELAY_MS : 1000}
                    max={CONFIG.MAX_DELAY_MS}
                    value={localDelay}
                    readOnly
                    className="delay-input delay-input-readonly"
                  />
                  <div className="quick-actions">
                    <button
                      type="button"
                      className={`quick-btn quick-btn-danger ${localDelay === 0 ? 'active danger-active' : ''} ${!isUnsafeDelayEnabled ? 'disabled' : ''}`}
                      onClick={() => handleDelayChange(0)}
                      disabled={!isUnsafeDelayEnabled}
                      title={isUnsafeDelayEnabled ? "Χωρίς καθυστέρηση - Υψηλός κίνδυνος rate limiting" : "Απαιτείται κωδικός πρόσβασης"}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      0ms
                    </button>
                    <button
                      type="button"
                      className={`quick-btn quick-btn-warning ${localDelay === 500 ? 'active warning-active' : ''} ${!isUnsafeDelayEnabled ? 'disabled' : ''}`}
                      onClick={() => handleDelayChange(500)}
                      disabled={!isUnsafeDelayEnabled}
                      title={isUnsafeDelayEnabled ? "500ms - Μέτριος κίνδυνος rate limiting" : "Απαιτείται κωδικός πρόσβασης"}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      500ms
                    </button>
                    <button
                      type="button"
                      className={`quick-btn quick-btn-yellow ${localDelay === 1000 ? 'active yellow-active' : ''}`}
                      onClick={() => handleDelayChange(1000)}
                      title="1 δευτερόλεπτο - Προεπιλεγμένη"
                    >
                      1s
                    </button>
                    <button
                      type="button"
                      className={`quick-btn quick-btn-safe ${localDelay === 3000 ? 'active safe-active' : ''}`}
                      onClick={() => handleDelayChange(3000)}
                      title="3 δευτερόλεπτα - Ασφαλής"
                    >
                      3s
                    </button>
                    <button
                      type="button"
                      className={`quick-btn quick-btn-safest ${localDelay === 5000 ? 'active safest-active' : ''}`}
                      onClick={() => handleDelayChange(5000)}
                      title="5 δευτερόλεπτα - Ασφαλέστερη επιλογή"
                    >
                      5s
                    </button>
                  </div>
                </div>
              </div>

              <div className="password-protection">
                <label htmlFor="unsafe-delay-password">Κωδικός για 0ms/500ms:</label>
                <p className="password-description">
                  Οι επιλογές 0ms και 500ms καθυστέρησης έχουν υψηλό κίνδυνο rate limiting και μπορεί να προκαλέσουν προβλήματα με το API. 
                  Για να ενεργοποιήσετε αυτές τις επιλογές, απαιτείται ειδικός κωδικός πρόσβασης.
                </p>
                <input
                  id="unsafe-delay-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Εισάγετε κωδικό"
                  className="password-input"
                />
                {!isUnsafeDelayEnabled && (password.length > 0) && (
                  <span className="password-error">Λάθος κωδικός</span>
                )}
                {isUnsafeDelayEnabled && (
                  <span className="password-success">Ενεργοποιημένο - Οι επιλογές 0ms και 500ms είναι πλέον διαθέσιμες</span>
                )}
              </div>

              <div className="delay-info">
                {(() => {
                  const badge = getDelayInfoBadge(localDelay);
                  return (
                    <div className={badge.className}>
                      {badge.icon}
                      {badge.text}
                  </div>
                  );
                })()}
              </div>

              <button
                type="button"
                className="reset-btn"
                onClick={resetToDefault}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                </svg>
                Επαναφορά στην Προεπιλεγμένη
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettings;

